import React, { createContext, useContext, useState, useCallback } from 'react';
import { FiAlertTriangle, FiTrash2, FiInfo } from 'react-icons/fi';

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);
  // state: { message, title, variant, resolve }

  const confirm = useCallback((message, { title, variant = 'danger' } = {}) => {
    return new Promise((resolve) => {
      setState({ message, title, variant, resolve });
    });
  }, []);

  const handleClose = (result) => {
    state?.resolve(result);
    setState(null);
  };

  const VARIANTS = {
    danger:  { icon: <FiTrash2 />,         iconBg: '#fee2e2', iconColor: '#ef4444', btnBg: '#ef4444' },
    warning: { icon: <FiAlertTriangle />,   iconBg: '#fef3c7', iconColor: '#f59e0b', btnBg: '#f59e0b' },
    info:    { icon: <FiInfo />,            iconBg: '#dbeafe', iconColor: '#3b82f6', btnBg: '#3b82f6' },
  };
  const v = VARIANTS[state?.variant] || VARIANTS.danger;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, padding: 16,
          animation: 'fadeIn 0.15s ease',
        }}>
          <style>{`@keyframes fadeIn{from{opacity:0;transform:scale(0.95)}to{opacity:1;transform:scale(1)}}`}</style>
          <div style={{
            background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 400,
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            animation: 'fadeIn 0.15s ease',
          }}>
            {/* Icon */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <div style={{
                width: 52, height: 52, borderRadius: '50%',
                background: v.iconBg, color: v.iconColor,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 22,
              }}>
                {v.icon}
              </div>
            </div>

            {/* Title */}
            <div style={{ textAlign: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>
                {state.title || 'Are you sure?'}
              </div>
            </div>

            {/* Message */}
            <div style={{ textAlign: 'center', fontSize: 14, color: '#64748b', marginBottom: 24, lineHeight: 1.5 }}>
              {state.message}
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                style={{
                  flex: 1, padding: '10px 0', borderRadius: 10, border: '1.5px solid #e2e8f0',
                  background: '#fff', color: '#64748b', fontWeight: 600, fontSize: 14, cursor: 'pointer',
                }}
                onClick={() => handleClose(false)}
              >
                Cancel
              </button>
              <button
                style={{
                  flex: 1, padding: '10px 0', borderRadius: 10, border: 'none',
                  background: v.btnBg, color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer',
                }}
                onClick={() => handleClose(true)}
                autoFocus
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used inside ConfirmProvider');
  return ctx;
}
